import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Circle, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

// OpenStreetMap estándar (política de uso: tráfico moderado, atribución obligatoria).
const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors';
// España completa, incluidas Baleares y Canarias.
export const SPAIN_BOUNDS = [[27.4, -18.4], [44.0, 4.6]];
// En pantallas estrechas se centra en la Península y Baleares (Canarias queda accesible desplazando).
const PENINSULA_BOUNDS = [[35.9, -9.4], [43.8, 4.4]];
const spainBounds = (map) => (map.getSize().x < 640 ? PENINSULA_BOUNDS : SPAIN_BOUNDS);

// Colores por fase (estado «cerrado» prevalece). Azul oscuro corporativo para ESTUDIO.
export const FASE_COLORS = {
  nuevo: { bg: '#FFFFFF', fg: '#0F172A', label: 'Nuevo' },
  estudio: { bg: '#2563EB', fg: '#FFFFFF', label: 'Estudio' }, // mismo azul que .btn-primary (--color-blue-main)
  mediacion: { bg: '#7C3AED', fg: '#FFFFFF', label: 'Mediación' },
  en_gestion: { bg: '#C026D3', fg: '#FFFFFF', label: 'En Gestión' },
  oferta: { bg: '#DC2626', fg: '#FFFFFF', label: 'Oferta' },
  compra: { bg: '#16A34A', fg: '#FFFFFF', label: 'Proceso compra / Adquirido' },
  cerrado: { bg: '#000000', fg: '#FFFFFF', label: 'Cerrado' },
  mixto: { bg: '#64748B', fg: '#FFFFFF', label: 'Mezcla de fases / sin fase' },
};

export const faseKey = (a) => {
  if (a.estado === 'cerrado') return 'cerrado';
  if (a.etapa_visual === 'proceso_compra' || a.etapa_visual === 'adquirido') return 'compra';
  return FASE_COLORS[a.etapa_visual] ? a.etapa_visual : 'mixto';
};

// Fase predominante (> 50 %) o neutro si hay mezcla.
const predominante = (counts) => {
  const total = Object.values(counts).reduce((x, y) => x + y, 0);
  const [k, n] = Object.entries(counts).sort((x, y) => y[1] - x[1])[0] || ['mixto', 0];
  return n * 2 > total ? k : 'mixto';
};

const countFases = (items) => items.reduce((acc, a) => { const k = faseKey(a); acc[k] = (acc[k] || 0) + 1; return acc; }, {});

const pinIcon = ({ count, order, selected, fase }) => {
  const c = FASE_COLORS[fase];
  return L.divIcon({
    className: '',
    html: `<div class="xen-pin ${selected ? 'xen-pin--sel' : ''}" style="background:${c.bg};color:${c.fg};border-color:${fase === 'nuevo' ? '#0F172A' : '#FFFFFF'}"><span>${order || (count > 1 ? count : '')}</span></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
  });
};

const clusterIcon = (cluster) => {
  const counts = {};
  cluster.getAllChildMarkers().forEach(m => Object.entries(m.options.faseCounts || {}).forEach(([k, n]) => { counts[k] = (counts[k] || 0) + n; }));
  const total = Object.values(counts).reduce((x, y) => x + y, 0);
  const c = FASE_COLORS[predominante(counts)];
  const size = total < 10 ? 34 : total < 100 ? 40 : 46;
  return L.divIcon({
    className: '',
    html: `<div class="xen-cluster" style="width:${size}px;height:${size}px;background:${c.bg};color:${c.fg}">${total}</div>`,
    iconSize: [size, size],
  });
};

const pointIcon = (label, tone) => L.divIcon({
  className: '',
  html: `<div class="xen-flag xen-flag--${tone}">${label}</div>`,
  iconSize: [26, 26],
  iconAnchor: [13, 13],
});

const ClusterLayer = ({ groups, selectedOrder, onGroupClick }) => {
  const map = useMap();
  const clickRef = useRef(onGroupClick);
  clickRef.current = onGroupClick;
  useEffect(() => {
    const cg = L.markerClusterGroup({ chunkedLoading: true, maxClusterRadius: 45, showCoverageOnHover: false, iconCreateFunction: clusterIcon });
    groups.forEach((g) => {
      const orders = g.items.map(i => selectedOrder.get(i.id)).filter(Boolean);
      const faseCounts = countFases(g.items);
      const m = L.marker([g.lat, g.lng], {
        faseCounts,
        icon: pinIcon({ count: g.items.length, order: orders.length ? orders.join('·') : null, selected: orders.length > 0, fase: predominante(faseCounts) }),
        title: g.items.map(i => i.codigo_npl || i.direccion).join(', '),
      });
      m.on('click', () => clickRef.current(g));
      cg.addLayer(m);
    });
    map.addLayer(cg);
    return () => { map.removeLayer(cg); };
  }, [map, groups, selectedOrder]);
  return null;
};

const FitToResults = ({ groups, fitKey }) => {
  const map = useMap();
  useEffect(() => {
    if (fitKey === 'spain' || !groups.length) { map.fitBounds(spainBounds(map)); return; }
    const b = L.latLngBounds(groups.map(g => [g.lat, g.lng]));
    map.fitBounds(b.pad(0.15), { maxZoom: 15 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, fitKey]);
  return null;
};

// Leaflet necesita recalcular su tamaño cuando cambia la altura del contenedor.
const AutoResize = () => {
  const map = useMap();
  useEffect(() => {
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(map.getContainer());
    return () => ro.disconnect();
  }, [map]);
  return null;
};

const ClickCapture = ({ onMapClick }) => {
  useMapEvents({ click: (e) => onMapClick && onMapClick({ lat: e.latlng.lat, lng: e.latlng.lng }) });
  return null;
};

const FlyTo = ({ target }) => {
  const map = useMap();
  useEffect(() => { if (target) map.flyTo([target.lat, target.lng], Math.max(map.getZoom(), 16), { duration: 0.6 }); }, [map, target]);
  return null;
};

const MapLegend = () => (
  <div className="absolute left-2 bottom-6 z-[400] rounded-lg shadow px-2.5 py-2 text-[11px] space-y-0.5 pointer-events-none hidden sm:block" style={{ background: 'rgba(255,255,255,0.95)', color: '#334155', border: '1px solid #CBD5E1' }} data-testid="map-legend">
    {Object.entries(FASE_COLORS).map(([k, c]) => (
      <div key={k} className="flex items-center gap-1.5">
        <span className="w-3 h-3 rounded-full shrink-0" style={{ background: c.bg, border: '1px solid #64748B' }} />{c.label}
      </div>
    ))}
    <div className="flex items-center gap-1.5 pt-0.5"><span className="w-3 h-3 rounded-full shrink-0" style={{ background: '#CBD5E1', boxShadow: '0 0 0 2px #22D3EE' }} />En la selección (nº de orden)</div>
  </div>
);

export const AssetsMap = ({ groups, selectedOrder, onGroupClick, onMapClick, fitKey, flyTarget, radius, extraPoints = [], picking, children, showLegend = true }) => (
  <div className={`relative h-full w-full ${picking ? 'xen-map-picking' : ''}`} data-testid="assets-map">
    <MapContainer bounds={SPAIN_BOUNDS} className="h-full w-full rounded-lg" scrollWheelZoom worldCopyJump={false} zoomSnap={0.25} zoomDelta={0.5}>
      <TileLayer url={TILE_URL} attribution={TILE_ATTR} maxZoom={19} />
      <ClusterLayer groups={groups} selectedOrder={selectedOrder} onGroupClick={onGroupClick} />
      <FitToResults groups={groups} fitKey={fitKey} />
      <AutoResize />
      <ClickCapture onMapClick={onMapClick} />
      <FlyTo target={flyTarget} />
      {children}
      {radius && <Circle center={[radius.lat, radius.lng]} radius={radius.km * 1000} pathOptions={{ color: '#2563eb', weight: 1, fillOpacity: 0.05 }} />}
      {extraPoints.map(p => (
        <Marker key={p.key} position={[p.lat, p.lng]} icon={pointIcon(p.label, p.tone)} />
      ))}
    </MapContainer>
    {showLegend && <MapLegend />}
  </div>
);

export default AssetsMap;
